import { useEffect, useRef } from 'react'
import { cinematicConfig } from '../config/scenes'
import { site } from '../config/site'
import { useReducedMotion } from '../hooks/useReducedMotion'
import { usePlanTripCta } from '../hooks/usePlanTripCta'
import { Icon } from './ui/Icon'

const { scenes } = cinematicConfig

/**
 * The hero's single background visual: a real travel clip (friends walking a
 * beach at sunset), scroll-scrubbed frame-wise — it never plays on its own.
 * The old poster-scrub stage (per-scene image layers + the full scrollWorld
 * engine) was removed in favour of this — scenes.js and lib/scrollWorld are
 * untouched for the reduced-motion journey list.
 */
const HERO_VIDEO = '/videos/roammyway-hero-travel.mp4'

/**
 * `adaptive` renders theme-aware colours (dark text on the light stage, white
 * on dark) for the GIF opener, whose background follows the theme. The
 * default keeps the always-white treatment for heroes that sit on dark
 * imagery in both themes (StaticHero posters, the parked scrub video).
 */
function HeroCopy({ adaptive = false }) {
  const { headline, highlight, body, cta, support } = site.hero
  const planTripCta = usePlanTripCta()
  return (
    <>
      <p
        className={`text-[0.72rem] font-bold tracking-[0.22em] uppercase sm:text-sm ${
          adaptive ? 'text-brand-600 dark:text-white/70' : 'text-white/70'
        }`}
      >
        {scenes[0].overlay.join(' ')}
      </p>
      <h1
        className={`mt-5 text-[clamp(2.4rem,5.6vw,4.6rem)] leading-[1] font-bold ${
          adaptive ? 'text-[var(--text-strong)]' : 'text-white'
        }`}
      >
        {headline[0]}
        <br />
        <span className={adaptive ? 'text-brand-600 dark:text-brand-300' : 'text-brand-300'}>{highlight}</span>
      </h1>
      <p
        className={`mt-6 max-w-[46ch] text-base leading-relaxed sm:text-lg ${
          adaptive ? 'text-[var(--text-body)]' : 'text-white/80'
        }`}
      >
        {body}
      </p>
      <div className="mt-8 flex flex-wrap items-center gap-4">
        <a
          href={planTripCta.href ?? cta.href}
          onClick={planTripCta.onClick}
          className={`group inline-flex items-center gap-2.5 rounded-full px-7 py-4 text-base font-semibold shadow-xl transition-[background-color,transform] duration-200 hover:-translate-y-0.5 ${
            adaptive
              ? 'bg-brand-600 text-white shadow-brand-600/25 hover:bg-brand-700 dark:bg-white dark:text-brand-700 dark:shadow-black/25 dark:hover:bg-brand-50'
              : 'bg-white text-brand-700 shadow-black/25 hover:bg-brand-50'
          }`}
        >
          <Icon name="send" className="h-[1.15rem] w-[1.15rem]" />
          {cta.label}
        </a>
        <a
          href="#how-it-works"
          className={`group inline-flex items-center gap-2.5 rounded-full border px-6 py-4 text-base font-semibold backdrop-blur-sm transition-[background-color,transform] duration-200 hover:-translate-y-0.5 ${
            adaptive
              ? 'border-[var(--border-subtle)] bg-[var(--surface)]/60 text-[var(--text-strong)] hover:bg-[var(--surface-tint)] dark:border-white/20 dark:bg-white/10 dark:text-white dark:hover:bg-white/15'
              : 'border-white/20 bg-white/10 text-white hover:bg-white/15'
          }`}
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full border border-current">
            <svg viewBox="0 0 24 24" className="h-3 w-3 translate-x-px fill-current" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
          See How It Works
        </a>
      </div>
      <p className={`mt-5 max-w-[42ch] text-sm ${adaptive ? 'text-[var(--text-muted)]' : 'text-white/65'}`}>
        {support}
      </p>
    </>
  )
}

/**
 * Reduced-motion path: no autoplaying video, no scroll-driven animation, no
 * multi-viewport scroll range. A single static hero, then the journey told as
 * an ordinary scrolling list.
 */
function StaticHero() {
  return (
    <section id="hero" className="relative">
      <div className="relative flex min-h-[92vh] items-center overflow-hidden">
        <picture>
          {scenes[0].posterMobile && (
            <source media="(max-width: 860px) and (orientation: portrait)" srcSet={scenes[0].posterMobile} />
          )}
          <img
            src={scenes[0].poster}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            fetchPriority="high"
          />
        </picture>
        <div className="absolute inset-0 bg-linear-to-r from-ink-950/85 via-ink-950/55 to-transparent" />
        <div className="relative mx-auto w-full max-w-[88rem] px-5 pt-28 pb-16 sm:px-8">
          <div className="max-w-3xl">
            <HeroCopy />
          </div>
        </div>
        <div data-header-sentinel className="absolute inset-x-0 bottom-0 h-px" aria-hidden="true" />
      </div>

      <div className="mx-auto max-w-[88rem] px-5 py-20 sm:px-8">
        <h2 className="sr-only">The RoamMyWay journey</h2>
        <ol className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {scenes.map((scene) => (
            <li key={scene.id} className="overflow-hidden rounded-2xl border border-[var(--border-subtle)]">
              <img src={scene.poster} alt="" className="aspect-16/9 w-full object-cover" loading="lazy" />
              <div className="p-6">
                <p className="font-mono text-xs tracking-[0.2em] text-brand-600">{scene.number}</p>
                <h3 className="mt-3 text-xl font-semibold text-accent-500 uppercase">{scene.overlay.join(' ')}</h3>
                <p className="mt-2 text-[0.95rem]">{scene.description}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

/**
 * PARKED, intentionally unmounted (like Pricing): the frame-wise scroll-
 * scrubbed video hero. The user asked for the airplane GIF opener first and
 * will say when to re-add this "video animation" below the start — mount it
 * again then. Do not delete as dead code.
 */
export function ScrubVideoHero() {
  const reducedMotion = useReducedMotion()
  const containerRef = useRef(null)
  const videoRef = useRef(null)
  const copyRef = useRef(null)
  const hintRef = useRef(null)

  // Frame-wise scroll scrubbing: scroll progress IS the video timeline. The
  // clip never plays on its own — scrolling down runs 0s → duration, scrolling
  // up runs it in reverse, stopping mid-scroll freezes that exact frame.
  //
  // Built with the same techniques as lib/scrollWorld's scrub engine (no GSAP
  // in this project, deliberately):
  //  - the clip is fetched as a Blob first, so it is always fully seekable
  //    with no dependency on HTTP byte-range support
  //  - duration is read from loadedmetadata, never hard-coded
  //  - the seek target is eased a little each frame, and a new currentTime is
  //    never assigned while the decoder is still seeking — a fast flick can't
  //    pile seeks up and freeze the video
  //  - the rAF loop runs only while the eased time is still converging; at
  //    rest it costs nothing
  // A very subtle scale (1.02 → 1.08) rides on top; the real animation is the
  // video frames themselves.
  useEffect(() => {
    if (reducedMotion) return
    const container = containerRef.current
    const video = videoRef.current
    if (!container || !video) return

    let objectUrl = null
    let cancelled = false
    let duration = 0
    let current = 0
    let target = 0
    let lastSet = -1
    let raf = 0

    const progress = () => {
      const rect = container.getBoundingClientRect()
      const range = rect.height - window.innerHeight
      return range > 0 ? Math.min(1, Math.max(0, -rect.top / range)) : 0
    }

    const tick = () => {
      raf = 0
      const p = progress()
      video.style.transform = `scale(${(1.02 + p * 0.06).toFixed(4)})`
      if (copyRef.current) {
        copyRef.current.style.transform = `translateY(${(-100 * p).toFixed(1)}px)`
        copyRef.current.style.opacity = String(Math.max(0, 1 - p * 1.33))
      }
      if (hintRef.current) hintRef.current.style.opacity = String(Math.max(0, 1 - p * 4))

      if (!duration) return
      target = p * duration
      current += (target - current) * 0.22
      if (Math.abs(target - current) < 0.003) current = target
      if (!video.seeking && Math.abs(current - lastSet) > 1 / 60) {
        video.currentTime = current
        lastSet = current
      }
      // Keep converging until the eased time lands and the decoder is idle.
      if (current !== target || video.seeking) schedule()
    }
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(tick)
    }

    const onMetadata = () => {
      duration = video.duration || 0
      // Land directly on the frame the current scroll position asks for —
      // frame 0 at the top of the page, mid-journey after a mid-page reload.
      current = target = progress() * duration
      lastSet = -1
      schedule()
    }

    video.addEventListener('loadedmetadata', onMetadata)
    video.addEventListener('seeked', schedule)
    fetch(HERO_VIDEO)
      .then((res) => res.blob())
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        video.src = objectUrl
      })
      .catch(() => {
        // Blob fetch failed (offline dev quirk etc.) — fall back to the URL
        // and rely on range requests for seeking.
        if (!cancelled) video.src = HERO_VIDEO
      })

    tick()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      cancelled = true
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      video.removeEventListener('loadedmetadata', onMetadata)
      video.removeEventListener('seeked', schedule)
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [reducedMotion])

  if (reducedMotion) return <StaticHero />

  return (
    <section id="hero" ref={containerRef} className="relative h-[380vh] md:h-[480vh]" aria-label="RoamMyWay — plan your perfect trip">
      <div data-header-sentinel className="absolute inset-x-0 bottom-0 h-px" aria-hidden="true" />
      <div className="sticky top-0 h-screen overflow-hidden bg-ink-950">
        {/* The one and only hero visual — no src here: the scrub effect feeds
            it a Blob URL, and no autoplay/loop: scroll owns the timeline. */}
        <video
          ref={videoRef}
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
          tabIndex={-1}
          aria-hidden="true"
          style={{ transform: 'scale(1.02)' }}
          className="absolute inset-0 h-full w-full object-cover object-center will-change-transform"
        />

        {/* Cinematic scrim — heavy over the copy on the left, releasing to
            clear by mid-frame so the travellers, ocean and sunset keep their
            natural exposure. Phones read bottom-up instead. */}
        <div className="pointer-events-none absolute inset-0 z-30 bg-linear-to-t from-ink-950/85 via-ink-950/20 to-ink-950/35 md:bg-linear-to-r md:from-ink-950/85 md:via-ink-950/30 md:via-35% md:to-transparent md:to-70%" />
        {/* Keeps the transparent header readable over the bright sky. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-30 h-32 bg-linear-to-b from-ink-950/70 to-transparent" />

        {/* Copy layer */}
        <div className="pointer-events-none absolute inset-0 z-40">
          <div className="mx-auto flex h-full max-w-[88rem] items-end px-5 pt-24 pb-24 sm:px-8 md:items-center md:pb-16">
            <div ref={copyRef} className="pointer-events-auto w-full max-w-3xl will-change-[opacity,transform]">
              <HeroCopy />
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          ref={hintRef}
          className="pointer-events-none absolute inset-x-0 bottom-7 z-40 flex flex-col items-center gap-2 text-white/70"
          aria-hidden="true"
        >
          <span className="text-[0.65rem] font-semibold tracking-[0.22em] uppercase">Scroll</span>
          <svg viewBox="0 0 24 24" className="h-5 w-5 animate-bounce" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </div>
    </section>
  )
}

/**
 * The current hero opener: a transparent "plane tracing a route" sticker GIF
 * (145 frames, ~5.8s loop — a pin, a dashed route and a plane flying it,
 * sourced from the user-supplied "Travel Plane Sticker by Copenhagen Airport
 * (CPH).gif", copied to plane-route-sticker.gif) beside the hero copy on the
 * dark stage. Previously a 4-plane formation GIF; swapped on request. Its
 * flat, saturated colours (red pin, navy plane, amber dashes) read cleanly on
 * both the light and dark stage without the brightness/glow filters the
 * muted-grey formation GIF needed. The scrubbed travel video (ScrubVideoHero
 * above) is parked and will be re-added below this opener when the user asks.
 */
export function CinematicHero() {
  const reducedMotion = useReducedMotion()

  if (reducedMotion) return <StaticHero />

  return (
    <section id="hero" className="relative" aria-label="RoamMyWay — plan your perfect trip">
      {/* The stage follows the theme: white in light mode, ink-950 in dark. */}
      <div className="relative flex min-h-screen items-center overflow-hidden bg-[var(--surface)]">
        {/* Soft glow behind the plane so the stage has depth. */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_72%_45%,rgba(56,152,178,0.08),transparent_70%)] dark:bg-[radial-gradient(60%_50%_at_72%_45%,rgba(56,152,178,0.18),transparent_70%)]" />
        {/* Keeps the transparent header readable over the dark stage. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-32 bg-linear-to-b from-ink-950/70 to-transparent dark:block" />

        <div className="relative mx-auto grid w-full max-w-[88rem] items-center gap-10 px-5 pt-28 pb-24 sm:px-8 md:grid-cols-2 md:gap-6 md:pb-16">
          <div className="max-w-3xl">
            <HeroCopy adaptive />
          </div>
          <div className="flex justify-center md:justify-end">
            <img
              src="/videos/plane-route-sticker.gif"
              alt=""
              aria-hidden="true"
              fetchPriority="high"
              className="w-full max-w-[22rem] select-none drop-shadow-[0_14px_28px_rgba(13,34,48,0.18)] md:max-w-[26rem] dark:drop-shadow-[0_0_24px_rgba(13,34,48,0.55)]"
            />
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-7 flex flex-col items-center gap-2 text-[var(--text-muted)] dark:text-white/70"
          aria-hidden="true"
        >
          <span className="text-[0.65rem] font-semibold tracking-[0.22em] uppercase">Scroll</span>
          <svg viewBox="0 0 24 24" className="h-5 w-5 animate-bounce" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>

        <div data-header-sentinel className="absolute inset-x-0 bottom-0 h-px" aria-hidden="true" />
      </div>
    </section>
  )
}
